'use client'

import { Button } from '@/components/ui/button'
import { signOutAndRedirect } from '@/lib/sign-out'
import { LogOut, RefreshCw, ServerCrashIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'

/**
 * Reloading the document rather than resetting a boundary. This screen is
 * rendered from two places — `app/error.tsx` when a server render throws, and
 * `error-boundary.tsx` when a client render does — and a full navigation is the
 * one recovery that works for both: it re-runs the server render from scratch,
 * including the session check in the proxy, and discards any client state the
 * failure left behind. Resetting would only ever help the second case.
 */
function reload(): void {
  window.location.reload()
}

export default function Error() {
  const t = useTranslations()
  return (
    <div className="flex min-h-screen w-full flex-col items-center justify-center gap-4 text-center">
      <ServerCrashIcon className="w-10 text-gray-400" />
      <h2 className="text-xl font-semibold">{t('error_500_title')}</h2>
      <p className="max-w-2xl text-center">{t('error_500_description')}</p>
      <h2 className="text-xl font-semibold">{t('error_500_footer')}</h2>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {/* Primary: most of what reaches this page is a blip that is already
            over by the time it is read, and retrying costs the user nothing. */}
        <Button onClick={reload}>
          <RefreshCw className="mr-2 size-4" />
          {t('error_try_again')}
        </Button>
        <Button variant="outline" onClick={signOutAndRedirect}>
          <LogOut className="mr-2 size-4" />
          {t('reboot_the_universe')}
        </Button>
      </div>
    </div>
  )
}
